/**
 * Conversation Recording Page
 *
 * Records doctor-patient conversations with speaker diarization.
 * Uses the same audio pipeline as dictation mode.
 */

import { MicrophoneNotice } from '@careflux/primitives/microphone';

import { LanguageSelector } from '@/components/LanguageSelector';
import { Header, Sidebar } from '@/components/layout';
import type { AttributedSummaryData } from '@/components/session/AttributedSummary';
import { AudioUpload } from '@/components/session/AudioUpload';
import { type ConversationMessage, ConversationTranscript } from '@/components/session/ConversationTranscript';
import { ModeSwitcher } from '@/components/session/ModeSwitcher';
import { PatientSearchSelect } from '@/components/session/PatientSearchSelect';
import { PipelineStallBanner } from '@/components/session/PipelineStallBanner';
import { RecordingControls } from '@/components/session/RecordingControls';
import { RecordingSilenceBanner } from '@/components/session/RecordingSilenceBanner';
import { SessionBottomBar, type SessionBottomBarAudioHandle } from '@/components/session/SessionBottomBar';
import { MobileAssistantShell, MobileSessionNavigation, SessionViewport } from '@/components/session/SessionPageLayout';
import { sessionLayoutClasses } from '@/components/session/SessionPageLayout.styles';
import { shouldShowCompactRecordingControl } from '@/components/session/recordingControlVisibility';
import { SmartPanel } from '@/components/session/smart-panel';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { hasBetaAccess, hasVerkefniAccess, useAuth } from '@/contexts/AuthContext';
import { useRecordingState } from '@/contexts/RecordingContext';
import { toOrderPatient } from '@/features/blood-tests/resolveOrderPatient';
import { useGenerateSummary } from '@/hooks/useGenerateSummary';
import { useNoteInstructions } from '@/hooks/useNoteInstructions';
import { useSession } from '@/hooks/useSession';
import { useSummaryGeneration } from '@/hooks/useSummaryGeneration';
import { useTasks } from '@/hooks/useTasks';
import { useTemplateDefaults } from '@/hooks/useTemplateDefaults';
import { useSaveUserSummary } from '@/lib/api';
import {
  applyConversationMessageUpdate,
  clearJustRefined,
  isNonSpeechMarker,
  upsertConversationMessage,
} from '@/lib/liveTranscript';
import type { Document } from '@/lib/mockData';
import { getSpeakerTitles } from '@/lib/speakerTitles';
import {
  type ConversationMessageEvent,
  type ConversationMessageUpdateEvent,
  type ConversationReviewEvent,
  type ConversationSummaryEvent,
  type SmartReviewCompleteEvent,
  StreamingClient,
  type TranscriptionResult,
} from '@/lib/streamingClient';
import { summaryEchoForDraft, summaryEchoForLoadedSession } from '@/lib/summaryGeneration';
import { captureVisitPCM } from '@/lib/visitStreamingCapture';
import { pcmToWav } from '@/lib/wavUtils';
import { messagesFromSegments } from '@/pages/conversationRestore';
import type { CallRecordingInput } from '@/types/callRecordingInput';
import { Loader2, Plus, Settings } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation } from 'wouter';

import type { SessionTutorialDemo } from '@/features/session-tutorial/sessionTutorial.types';
import { demoMessageId, isDemoMessageId } from '@/features/session-tutorial/sessionTutorialDemoContent';
import { useTutorialDemoNote } from '@/features/session-tutorial/useTutorialDemoNote';
// New hooks and utilities
import {
  useAudioDevices,
  useLanguages,
  usePanelResize,
  usePatientSearch,
  useRecordingTimer,
  useSettings,
  useStreamingRecorder,
  useToast,
} from '@/hooks';
import { formatTime } from '@/lib/sessionUtils';
import { convertTurnsToMessages, mergeSmartReviewMessages } from '@/lib/smartReviewUtils';
import type { SummaryStyle } from '@/types/summaryStyle';
import { useDictationRecordingSignal } from './dictationRecordingSignal';

interface ConversationProps {
  sessionId?: string; // Optional session ID for resuming existing conversation
  call?: CallRecordingInput;
}

export default function Conversation({ sessionId: resumeSessionId, call }: ConversationProps = {}) {
  const [, navigate] = useLocation();
  const { toast } = useToast();
  const { user, isViewingAsDelegate, viewingAsAuthAccountId } = useAuth();
  // The composer panel's column layout is beta-only, like the panel itself.
  const layoutClasses = sessionLayoutClasses[hasBetaAccess(user) ? 'beta' : 'legacy'];

  // Use custom hooks for shared functionality
  const { devices, selectedDeviceId, setSelectedDeviceId, microphone, retryMicrophone } = useAudioDevices(!call);
  const { languages, selectedLanguage, setSelectedLanguage, selectedSummaryLanguage, setSelectedSummaryLanguage } =
    useLanguages();
  const { asrModelPreference } = useSettings();
  const patientSearch = usePatientSearch({ resumeSessionId, loadSavedPatient: true });
  const { selectedPatient, selectedPatientRef, clearSelectedPatient, attachPatientDbId } = patientSearch;
  const bloodTestPatient = toOrderPatient(selectedPatient);
  const { rightPanelWidth, isResizing: _isResizing, handleMouseDown } = usePanelResize('smartPanelWidth');
  const { setRecording } = useRecordingState();

  // Recording state
  const callRef = useRef(call);
  callRef.current = call;
  const callEpoch = useRef(0);
  const callClient = useRef<StreamingClient | null>(null);
  const callCapture = useRef<ReturnType<typeof captureVisitPCM> | null>(null);
  const [isRecording, setIsRecording] = useState(false);
  useDictationRecordingSignal(isRecording, setRecording);
  const isActivelyRecordingRef = useRef(false); // True when capturing audio (not paused)
  const [isInitializing, setIsInitializing] = useState(false); // True while connecting/setting up
  const [isPaused, setIsPaused] = useState(false); // True when recording is paused (audio capture stopped but session open)

  // Use timer hook - timer stops when paused
  const { elapsedTime, setElapsedTime } = useRecordingTimer(isRecording, isInitializing, isPaused);
  const [mobileView, setMobileView] = useState<'recording' | 'assistant'>('recording');

  // Finalization state - when waiting for final transcription after stopping
  const [isFinalizingTranscription, setIsFinalizingTranscription] = useState(false);
  const messagesCountAtRecordingStartRef = useRef<number>(0);

  // Smart review state - when AI is reviewing/cleaning up the transcript
  const [isSmartReviewing, setIsSmartReviewing] = useState(false);

  // Resume/loading state
  const [isLoadingSession, setIsLoadingSession] = useState(!!resumeSessionId);
  const [isResumedSession, setIsResumedSession] = useState(false);

  // Clinic settings state - controls whether audio is uploaded to blob storage
  // Default to false (fail-closed) until we fetch the actual clinic setting
  const [storeAudioInBlob, setStoreAudioInBlob] = useState(false);

  // Conversation state
  const [messages, setMessages] = useState<ConversationMessage[]>([]);
  const messagesRef = useRef<ConversationMessage[]>([]);
  // Keep messagesRef in sync with state (for use in callbacks to avoid stale closures)
  useEffect(() => {
    messagesRef.current = messages;
  }, [messages]);
  const [provisionalText, setProvisionalText] = useState('');
  // Ephemeral pre-encounter context typed by the clinician (PMH, meds,
  // complaints). Stored in sessionStorage by SamhengiPanel; mirrored here as
  // a ref so the summary-generation fetch picks up the freshest value. The
  // setter is wrapped in useCallback so SamhengiPanel's `setAndNotify` memo
  // stays stable across parent rerenders.
  const preEncounterContextRef = useRef<string>('');
  const handlePreEncounterContextChange = useCallback((text: string) => {
    preEncounterContextRef.current = text;
  }, []);

  // Template state — the server-persisted default seeds the selection until
  // the user makes an explicit pick this session.
  const { conversation: defaultConversationTemplateId, setTemplateDefault } = useTemplateDefaults();
  const [selectedTemplateId, setSelectedTemplateId] = useState<number | null>(null);
  const hasPickedTemplateRef = useRef(false);

  useEffect(() => {
    if (hasPickedTemplateRef.current) return;
    if (defaultConversationTemplateId != null) {
      setSelectedTemplateId(defaultConversationTemplateId);
    }
  }, [defaultConversationTemplateId]);

  // Restoring a session's saved template counts as a pick: the server default
  // must not overwrite it if the defaults query resolves later.
  const restoreTemplateSelection = (templateId: number) => {
    hasPickedTemplateRef.current = true;
    setSelectedTemplateId(templateId);
  };

  // Handle template change from SmartPanel. `persist: false` means the panel
  // is clearing a selection it couldn't resolve in this org — update the local
  // selection but never write that to the server-side default.
  const handleTemplateChange = (templateId: number | null, options?: { persist?: boolean }) => {
    hasPickedTemplateRef.current = true;
    setSelectedTemplateId(templateId);
    if (options?.persist !== false) {
      setTemplateDefault('conversation', templateId);
    }
  };

  // Smart panel state
  const [summary, setSummary] = useState('');
  const [documents, _setDocuments] = useState<Document[]>([]);
  const [isProcessing, _setIsProcessing] = useState(false);
  const [isGeneratingSocketSummary, setIsGeneratingSocketSummary] = useState(false);
  const { mutateAsync: requestSummary, isPending: isGeneratingHttpSummary } = useGenerateSummary();
  const isGeneratingSummary = isGeneratingSocketSummary || isGeneratingHttpSummary;
  const [summaryTemplateInfo, setSummaryTemplateInfo] = useState<{
    baseTemplate: string;
    clinicalTemplate: string | null;
  } | null>(null);

  // Summary editing state
  const [editedSummary, setEditedSummary] = useState<string | null>(null);
  const saveSummaryMutation = useSaveUserSummary();
  const copySummaryMutation = useSaveUserSummary();
  const isSavingSummary = saveSummaryMutation.isPending;
  const summaryGeneration = useSummaryGeneration();

  // Attribution state for linking summary to transcript/audio
  const [attributedSummary, setAttributedSummary] = useState<AttributedSummaryData | null>(null);
  const [highlightedSegmentIds, setHighlightedSegmentIds] = useState<string[] | null>(null);

  // Autosave summary draft to localStorage (debounced, 5 seconds after last edit)
  useEffect(() => {
    if (!editedSummary || !sessionIdRef.current) return;

    const timeoutId = setTimeout(() => {
      const draftKey = `summaryDraft_${sessionIdRef.current}`;
      localStorage.setItem(
        draftKey,
        JSON.stringify({
          text: editedSummary,
          timestamp: Date.now(),
          templateId: selectedTemplateId,
          generationId: summaryGeneration.generationId(),
        })
      );
    }, 5000); // 5 second debounce

    return () => clearTimeout(timeoutId);
  }, [editedSummary, selectedTemplateId, summaryGeneration]);

  // Clear draft from localStorage (called after explicit save)
  const clearSummaryDraft = () => {
    if (sessionIdRef.current) {
      localStorage.removeItem(`summaryDraft_${sessionIdRef.current}`);
    }
  };

  // Audio state
  const [recordedAudioUrl, setRecordedAudioUrl] = useState<string | null>(null);
  const bottomBarAudioRef = useRef<SessionBottomBarAudioHandle>(null);
  // Mic capture + stall recovery, silence auto-pause, connection toasts, wake lock
  const {
    pcmAudioManagerRef,
    streamingClientRef,
    startCapture,
    pauseCapture,
    resumeCapture,
    connectionState,
    handleConnectionStateChange,
    audioQuality,
    pipelineStalled,
    handleManualRecovery,
    silence: { phase: silencePhase, secondsRemaining: silenceSecondsRemaining, cancel: cancelSilenceCountdown },
  } = useStreamingRecorder({
    label: 'Conversation',
    isRecording,
    isPaused,
    onAutoStop: () => pauseRecording(),
    onSessionEnded: () => {
      // The ref gates the smart-review spinner and its error toast, so leaving
      // it set means the finalisation results we deliberately keep listening
      // for never reach the screen.
      isActivelyRecordingRef.current = false;
      setIsRecording(false);
      setIsPaused(false);
      setIsInitializing(false);
    },
    onMicrophoneError: microphone.reportError,
    onMicrophoneReady: microphone.markGranted,
  });
  // Session lifecycle (mint id + navigate + POST /api/sessions) is owned by the
  // shared hook; sessionIdRef is its ref so existing reads keep working.
  const { sessionIdRef, ensureSession } = useSession({
    mode: 'conversation',
    language: selectedLanguage,
    getPatient: () => selectedPatientRef.current ?? undefined,
    onPatientResolved: attachPatientDbId,
    resumeSessionId,
  });
  const messageIdRef = useRef<number>(0);

  // Note: Patient state (selectedPatient, recentPatients, etc.) is now handled by usePatientSearch hook

  // Handle finalization timeout - if no transcription arrives within 90 seconds, clear state
  useEffect(() => {
    let timeoutId: NodeJS.Timeout | null = null;

    if (isFinalizingTranscription) {
      // Set timeout to clear finalization state if no transcription arrives within 90 seconds
      timeoutId = setTimeout(() => {
        setIsFinalizingTranscription(false);
        // Disconnect WebSocket if still connected
        if (streamingClientRef.current) {
          streamingClientRef.current.disconnect();
          streamingClientRef.current = null;
        }
        toast({
          title: 'Umritun tókst ekki',
          description: 'Engin umritun barst. Reyndu aftur.',
          variant: 'destructive',
        });
      }, 90000); // 90 second timeout
    }
    // Note: Don't auto-disconnect when isFinalizingTranscription becomes false
    // The disconnect is handled in stopRecording after stopSession() completes

    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [isFinalizingTranscription, toast, streamingClientRef]);

  // Note: Language fetching is now handled by useLanguages hook

  // Load existing conversation when resuming a session
  // biome-ignore lint/correctness/useExhaustiveDependencies: setElapsedTime is a stable state setter, only run when resumeSessionId changes
  useEffect(() => {
    if (!resumeSessionId) return;

    async function loadExistingConversation() {
      try {
        const response = await fetch(`/api/sessions/${resumeSessionId}/live-conversation`, {
          credentials: 'include',
        });

        if (!response.ok) {
          throw new Error('Failed to load conversation');
        }

        const data = await response.json();
        patientSearch.reconcileWithSessionPatient(data.audio?.patient_id ?? null);

        // Segments are the single source of truth for the transcript (the
        // smart_review_result blob is retired). Corrected diarization lives on
        // each segment's speaker_label, so this reflects smart-review output.
        if (data.segments && data.segments.length > 0) {
          const loadedMessages = messagesFromSegments(data.segments);
          setMessages(loadedMessages);
          messageIdRef.current = loadedMessages.length;
        }

        // Set session ID
        sessionIdRef.current = resumeSessionId || '';
        setIsResumedSession(true);

        // Load patient info if available
        if (data.audio?.patient_ehr_id) {
        }

        // Set audio URL for playback if available
        if (data.audio?.audio_url) {
          setRecordedAudioUrl(data.audio.audio_url);
        }

        // Restore elapsed time from audio duration or last segment timestamp
        if (data.audio?.duration) {
          // Use stored audio duration (in seconds)
          setElapsedTime(Math.round(data.audio.duration));
        } else if (data.segments && data.segments.length > 0) {
          // Calculate from last segment's end_time
          const lastSegment = data.segments[data.segments.length - 1];
          const lastTime = lastSegment.end_time || lastSegment.start_time || 0;
          setElapsedTime(Math.round(lastTime));
        }

        // Load existing summary - prefer user_summary if available, otherwise model_summary
        // Note: model_summary can be in separate table (model_summary.summary_text) or in audio record (audio.model_summary)
        // Echo the generation behind whichever text ends up displayed below, so
        // saves after reopening pair with that generation (or reset, if the
        // displayed text came from an audio-record fallback with no known id).
        summaryGeneration.recordFromHttp(summaryEchoForLoadedSession(data), null);
        let loadedSummary = '';
        if (data.user_summary?.summary_text) {
          loadedSummary = data.user_summary.summary_text;
          setSummary(loadedSummary);
          // Also set template if available
          if (data.user_summary.selected_template_id) {
            restoreTemplateSelection(data.user_summary.selected_template_id);
          }
          // Restore attributed summary for hover-to-highlight functionality
          if (data.user_summary.summary_attributed) {
            setAttributedSummary(data.user_summary.summary_attributed);
          }
        } else if (data.model_summary?.summary_text) {
          loadedSummary = data.model_summary.summary_text;
          setSummary(loadedSummary);
          // Also set template if available
          if (data.model_summary.selected_template_id) {
            restoreTemplateSelection(data.model_summary.selected_template_id);
          }
          // Restore attributed summary for hover-to-highlight functionality
          if (data.model_summary.summary_attributed) {
            setAttributedSummary(data.model_summary.summary_attributed);
          }
        } else if (data.audio?.user_summary) {
          // Fallback: user_summary stored directly in audio record (preferred over model_summary)
          loadedSummary = data.audio.user_summary;
          setSummary(loadedSummary);
          if (data.audio.selected_template_id) {
            restoreTemplateSelection(data.audio.selected_template_id);
          }
        } else if (data.audio?.model_summary) {
          // Fallback: model_summary stored directly in audio record
          loadedSummary = data.audio.model_summary;
          setSummary(loadedSummary);
          if (data.audio.selected_template_id) {
            restoreTemplateSelection(data.audio.selected_template_id);
          }
        }

        // Check for localStorage draft (autosaved work in progress)
        const draftKey = `summaryDraft_${resumeSessionId}`;
        const draftJson = localStorage.getItem(draftKey);
        if (draftJson) {
          try {
            const draft = JSON.parse(draftJson);
            // Only restore if draft is different from loaded summary
            if (draft.text && draft.text !== loadedSummary) {
              setEditedSummary(draft.text);
              summaryGeneration.recordFromHttp(summaryEchoForDraft(draft), null);
              if (draft.templateId) {
                restoreTemplateSelection(draft.templateId);
              }
              toast({
                title: 'Drög endurheimt',
                description: 'Óvistaðar breytingar fundust og voru endurheimt.',
              });
            }
          } catch (e) {
            console.error('[Conversation] Error parsing localStorage draft:', e);
          }
        }
      } catch (error) {
        patientSearch.reconcileWithSessionPatient(null);
        console.error('[Conversation] Error loading session:', error);
        toast({
          title: 'Villa',
          description: 'Gat ekki hlaðið samtali',
          variant: 'destructive',
        });
      } finally {
        setIsLoadingSession(false);
      }
    }

    loadExistingConversation();
  }, [resumeSessionId, toast]);

  // Save panel width to localStorage
  useEffect(() => {
    localStorage.setItem('smartPanelWidth', rightPanelWidth.toString());
  }, [rightPanelWidth]);

  // Auto-regenerate summary when summary language changes (if there's existing messages and summary)
  const prevSummaryLanguageRef = useRef(selectedSummaryLanguage);
  // biome-ignore lint/correctness/useExhaustiveDependencies: Only want to trigger on language change, not other deps
  useEffect(() => {
    // Only regenerate if language actually changed (not on initial mount)
    if (prevSummaryLanguageRef.current !== selectedSummaryLanguage) {
      prevSummaryLanguageRef.current = selectedSummaryLanguage;
      // Only regenerate if there are messages and a summary already exists
      if (messages.length > 0 && summary && !isGeneratingSummary && !isRecording) {
        generateSummary();
      }
    }
  }, [selectedSummaryLanguage]);

  // Note: Audio devices, timer, and formatTime are now handled by custom hooks/utils

  // Handle provisional transcription (instant feedback)
  const handleTranscription = (result: TranscriptionResult) => {
    try {
      const text = result.text.trim();
      if (!text || isNonSpeechMarker(text)) {
        return;
      }

      if (result.is_provisional) {
        // Show provisional text in live area (no speaker label)
        setProvisionalText(text);
      }
      // Final transcriptions in conversation mode come via conversation_message
    } catch (error) {
      console.error('[Conversation] Transcription error:', error);
    }
  };

  // Handle conversation messages from server (with speaker detection)
  const handleConversationMessage = (event: ConversationMessageEvent) => {
    try {
      const { message } = event;
      if (!message.text.trim()) {
        return;
      }

      // Add the message from the server (speaker already detected)
      setMessages((prev) => {
        const updated = upsertConversationMessage(prev, event, elapsedTime);
        return callRef.current
          ? updated.sort((a, b) => a.timestamp - b.timestamp || a.id.localeCompare(b.id))
          : updated;
      });
      setProvisionalText(''); // Clear provisional text when final message arrives

      // Clear finalization state when final message arrives (unless pending refinement)
      if (!event.is_pending_refinement) {
        setIsFinalizingTranscription(false);
      }

      // Segments are saved server-side via _schedule_segment_save — no client POST needed
    } catch (error) {
      console.error('[Conversation] Message error:', error);
    }
  };

  // Handle conversation message updates (text refinement from large model)
  const handleConversationMessageUpdate = (event: ConversationMessageUpdateEvent) => {
    try {
      setMessages((prev) => applyConversationMessageUpdate(prev, event));

      // Clear finalization state when refinement complete
      if (!event.is_pending_refinement) {
        setIsFinalizingTranscription(false);
      }

      // Segments are saved server-side via _schedule_segment_save — no client POST needed

      // Clear wasJustRefined after animation duration (500ms)
      if (event.was_refined) {
        setTimeout(() => setMessages((prev) => clearJustRefined(prev, event.message_id)), 500);
      }
    } catch (error) {
      console.error('[Conversation] Message update error:', error);
    }
  };

  // Handle conversation review (speaker corrections from Gemini)
  const handleConversationReview = (event: ConversationReviewEvent) => {
    try {
      if (event.corrections_count === 0 && !event.is_final) {
        // No corrections needed, skip update
        return;
      }

      // Replace all messages with corrected versions
      const correctedMessages: ConversationMessage[] = event.messages.map((m) => ({
        id: m.id,
        speaker: m.speaker,
        text: m.text,
        timestamp: m.timestamp || 0,
      }));

      setMessages(correctedMessages);

      // Corrected segments are saved to DB server-side after smart review.
      // No client-side PATCHes needed.

      if (event.corrections_count > 0) {
        toast({
          title: 'Ræðumaður leiðréttur',
          description: `${event.corrections_count} skilaboð voru leiðrétt`,
          duration: 2000,
        });
      }
    } catch (error) {
      console.error('[Conversation] Review error:', error);
    }
  };

  // Handle conversation summary
  const handleConversationSummary = (event: ConversationSummaryEvent) => {
    setSummary(event.summary);
    summaryGeneration.recordFromEvent(event);
    // The new generation replaces whatever was on screen — any pending edit was
    // made against the old text, so it must never be saved paired with this id.
    setEditedSummary(null);
    // Handle template_used which can be string or object
    if (typeof event.template_used === 'object' && event.template_used !== null) {
      setSummaryTemplateInfo(event.template_used);
    }
    setIsGeneratingSocketSummary(false); // Always reset generating state

    // Set attributed summary if available (for hover/click to transcript)
    if (event.attributed_summary) {
      setAttributedSummary(event.attributed_summary);
    }

    const templateName =
      typeof event.template_used === 'string'
        ? event.template_used
        : event.template_used?.clinicalTemplate || event.template_used?.baseTemplate || 'Unknown';
    toast({
      title: 'Samantekt tilbúin',
      description: `Sniðmát: ${templateName}`,
    });
  };

  // Handle smart review completion - updates transcript with cleaned text and split speakers
  const handleSmartReviewComplete = (event: SmartReviewCompleteEvent) => {
    setIsSmartReviewing(false);

    // Guard: if smart review returned no turns (model failure), don't touch messages
    if (!event.speaker_turns || event.speaker_turns.length === 0) {
      return;
    }

    // Use ref to get current messages count for toast
    const currentMessages = messagesRef.current;

    // Convert new speaker turns to ConversationMessage format
    const newMessages = convertTurnsToMessages(event.speaker_turns);

    // Replace original messages with smart-reviewed versions
    setMessages((prev) => mergeSmartReviewMessages(prev, newMessages, event.replaced_segment_ids));

    // Show success toast
    const originalCount = currentMessages.length;
    const newCount = originalCount + newMessages.length;
    toast({
      title: 'Umritun endurskoðuð',
      description:
        newCount > originalCount ? `${newMessages.length} nýir talsbútar búnir til` : 'Texti og ræðumenn hreinsaðir',
      duration: 3000,
    });
  };

  // Start recording
  const startRecording = async () => {
    const epoch = callRef.current ? ++callEpoch.current : callEpoch.current;
    let ownedCallClient: StreamingClient | null = null;
    try {
      // Track message count at start to detect new transcription
      messagesCountAtRecordingStartRef.current = messages.length;

      // A session with no id yet is a fresh one (vs resuming an existing session).
      const isNewSession = !sessionIdRef.current;
      // Mint id + navigate + create the session (idempotent) via the shared hook.
      const { patient: resolvedPatient } = await ensureSession();

      if (callRef.current && epoch !== callEpoch.current) return;

      // Clean up any existing streaming client (prevents zombie sockets if
      // startRecording is called without stopping the previous session)
      if (streamingClientRef.current) {
        streamingClientRef.current.disconnect();
      }

      // Initialize streaming client
      const client = new StreamingClient();
      streamingClientRef.current = client;
      if (callRef.current) {
        ownedCallClient = client;
        callClient.current = ownedCallClient;
      }

      // Set up callbacks
      client.onTranscription(handleTranscription); // For provisional text
      client.onConversationMessage(handleConversationMessage); // For final messages
      client.onConversationMessageUpdate(handleConversationMessageUpdate); // For text refinement
      client.onConversationReview(handleConversationReview); // For speaker corrections
      client.onConversationSummary(handleConversationSummary); // For template-based summary

      let handledCallWarning = false;
      client.onStreamingWarning((event) => {
        if (callRef.current && epoch === callEpoch.current && !event.audio_recovered && !handledCallWarning) {
          handledCallWarning = true;
          void callCapture.current?.abort();
          callCapture.current = null;
          setIsRecording(false);
          isActivelyRecordingRef.current = false;
          void ownedCallClient?.stopVisitStream(true).catch(() => {});
          callRef.current.onCaptureError();
        }
      });

      // Smart review callbacks
      // Only show spinner when not actively recording (incremental reviews run silently)
      client.onSmartReviewStart(() => {
        if (!isActivelyRecordingRef.current) {
          setIsSmartReviewing(true);
        }
      });
      client.onSmartReviewComplete(handleSmartReviewComplete);
      client.onSmartReviewError((event) => {
        console.error('[Conversation] Smart review error:', event.error);
        setIsSmartReviewing(false);
        if (!isActivelyRecordingRef.current) {
          toast({
            title: 'Villa í endurskoðun',
            description: 'Ekki tókst að endurskoða samtalið. Upprunalega umritunin helst óbreytt.',
            variant: 'destructive',
          });
        }
      });
      client.onTasksDetected((event) => {
        handleServerTasks(event);
      });

      // Connection health monitoring
      client.onConnectionStateChange(handleConnectionStateChange);

      await client.connect();
      if (callRef.current && epoch !== callEpoch.current) {
        client.disconnect();
        return;
      }

      // Start session in conversation mode with participant names for context.
      await client.startSession(
        sessionIdRef.current,
        selectedLanguage,
        resolvedPatient?.dbId?.toString(),
        'conversation', // Use conversation mode
        {
          doctorName: user?.name,
          patientName: resolvedPatient?.name,
          clinicalTemplateId: selectedTemplateId ?? undefined,
          fhirPatientId: resolvedPatient?.fhirPatientId,
          summaryLanguage: selectedSummaryLanguage,
          asrModelPreference,
          startReason: isNewSession ? 'new' : 'resume',
        }
      );

      if (callRef.current && epoch !== callEpoch.current) {
        client.disconnect();
        return;
      }

      // Start capturing, streaming PCM to the server
      if (callRef.current) {
        const input = callRef.current;
        if (!input.permitted || !input.room || !input.patientIdentity || epoch !== callEpoch.current) {
          throw new Error('Call input unavailable');
        }
        const generation = await client.startVisitStream(input.visitId);
        if (generation !== input.generation || epoch !== callEpoch.current) {
          await client.stopVisitStream(true);
          throw new Error('Call consent changed');
        }
        input.drain.current = async () => {
          await callCapture.current?.stop();
          callCapture.current = null;
          await client.stopVisitStream();
          setIsRecording(false);
          isActivelyRecordingRef.current = false;
        };
        callCapture.current = captureVisitPCM(
          input.room,
          input.patientIdentity,
          ({ role, chunk }) => client.sendVisitPCM(role, generation, chunk),
          () => {
            if (epoch !== callEpoch.current) return;
            setIsRecording(false);
            isActivelyRecordingRef.current = false;
            void client.stopVisitStream(true).catch(() => {});
            input.onCaptureError();
          }
        );
      } else {
        await startCapture(selectedDeviceId);
        microphone.markGranted();
      }

      // Only clear state for new sessions, preserve existing data when resuming
      if (isNewSession) {
        setMessages([]);
        setProvisionalText('');
        setSummary('');
        summaryGeneration.reset();
        setSummaryTemplateInfo(null);

        // Clear previous recording only for new sessions
        if (recordedAudioUrl) {
          URL.revokeObjectURL(recordedAudioUrl);
          setRecordedAudioUrl(null);
        }
      } else {
        // Resuming existing session - keep existing data, just clear provisional text
        setProvisionalText('');
      }

      setIsInitializing(false);

      toast({
        title: callRef.current ? 'Talgreining hafin' : 'Upptaka hafin',
        description: callRef.current
          ? 'Hljóð beggja viðmælenda er unnið í texta. Hljóð og mynd eru ekki vistuð.'
          : 'Samtal verður tekið upp...',
      });
    } catch (error) {
      if (callRef.current && epoch !== callEpoch.current) {
        ownedCallClient?.disconnect();
        return;
      }
      if (callRef.current) {
        void callCapture.current?.abort();
        callCapture.current = null;
        streamingClientRef.current?.disconnect();
        streamingClientRef.current = null;
        if (epoch === callEpoch.current) callRef.current.onCaptureError();
      }
      console.error('[Conversation] Start error:', error);
      setIsInitializing(false);
      setIsRecording(false);
      isActivelyRecordingRef.current = false;
      if (microphone.reportError(error)) {
        streamingClientRef.current?.disconnect();
        streamingClientRef.current = null;
        return;
      }
      toast({
        title: 'Villa',
        description: 'Ekki tókst að hefja upptöku',
        variant: 'destructive',
      });
    }
  };

  const startCallRef = useRef(startRecording);
  startCallRef.current = startRecording;
  useEffect(() => {
    if (
      !call?.permitted ||
      !call.room ||
      !call.patientIdentity ||
      typeof call.generation !== 'number' ||
      isLoadingSession
    )
      return;
    const original = callRef.current;
    if (!original) return;
    setIsRecording(true);
    setIsInitializing(true);
    isActivelyRecordingRef.current = true;
    void startCallRef.current();
    return () => {
      const cleanupEpoch = ++callEpoch.current;
      if (callRef.current) callRef.current.drain.current = null;
      const capture = callCapture.current;
      callCapture.current = null;
      const client = callClient.current;
      void (async () => {
        try {
          await capture?.stop();
          await client?.stopVisitStream();
        } catch {
          if (callEpoch.current === cleanupEpoch) original.onCaptureError();
        } finally {
          client?.disconnect();
        }
      })();
      callClient.current = null;
      isActivelyRecordingRef.current = false;
      setIsRecording(false);
      setIsInitializing(false);
      setProvisionalText('');
    };
  }, [call?.permitted, call?.room, call?.patientIdentity, call?.generation, isLoadingSession]);

  // Stop recording (kept for potential cleanup/error recovery, not used in normal flow)
  const _stopRecording = async () => {
    // Check if we already got messages during recording
    const _gotNewMessages = messages.length > messagesCountAtRecordingStartRef.current;

    // Create WAV file from recorded PCM data and upload to storage
    if (pcmAudioManagerRef.current) {
      try {
        const snap = pcmAudioManagerRef.current.getUploadSnapshot();
        const allPCM = snap.pcm;
        const sampleRate = snap.sampleRate;

        if (allPCM.length > 0) {
          // Calculate expected duration
          const _calculatedDuration = allPCM.length / sampleRate;

          const wavBlob = pcmToWav(allPCM, sampleRate);
          const wavUrl = URL.createObjectURL(wavBlob);

          // Clean up previous URL if exists
          if (recordedAudioUrl) {
            URL.revokeObjectURL(recordedAudioUrl);
          }

          setRecordedAudioUrl(wavUrl);

          // Upload audio directly to Azure Blob Storage (bypasses ingress limit)
          // Only upload if clinic setting allows it
          if (sessionIdRef.current && storeAudioInBlob) {
            try {
              const formData = new FormData();
              formData.append('file', wavBlob, 'complete.wav');
              formData.append('capture_epoch_id', snap.captureEpochId);
              formData.append('capture_start_sample', String(snap.captureStartSample));
              formData.append('capture_end_sample', String(snap.captureEndSample));
              formData.append('capture_sample_rate', String(snap.sampleRate));

              const uploadResp = await fetch(`/api/sessions/${sessionIdRef.current}/upload-audio`, {
                method: 'POST',
                credentials: 'include',
                body: formData,
              });

              if (uploadResp.ok) {
                const uploadData = await uploadResp.json();
                // Update URL to server URL for persistence across refreshes
                if (uploadData.audio_url) {
                  URL.revokeObjectURL(wavUrl);
                  setRecordedAudioUrl(uploadData.audio_url);
                }
              } else {
                const errorText = await uploadResp.text();
                console.error('[Conversation] Upload failed:', uploadResp.status, errorText);
              }
            } catch (uploadError) {
              console.error('[Conversation] Error uploading audio:', uploadError);
            }
          } else if (sessionIdRef.current && !storeAudioInBlob) {
          }
        }
      } catch (error) {
        console.error('[Conversation] Error creating WAV:', error);
      }
    }

    // Stop PCM audio manager
    if (pcmAudioManagerRef.current) {
      await pcmAudioManagerRef.current.stop();
      pcmAudioManagerRef.current = null;
    }

    // Stop streaming session - this flushes the buffer and processes remaining audio
    if (streamingClientRef.current) {
      await streamingClientRef.current.stopSession(selectedSummaryLanguage);

      // Wait for any pending conversation_message events to arrive
      // The backend processes the buffer and sends transcription after stopSession
      await new Promise((resolve) => setTimeout(resolve, 3000));

      // Clear finalization state and disconnect
      setIsFinalizingTranscription(false);
      if (streamingClientRef.current) {
        streamingClientRef.current.disconnect();
        streamingClientRef.current = null;
      }
    }

    setProvisionalText('');
  };

  // Pause audio capture (keeps session open for resume)
  const pauseRecording = async () => {
    if (!pcmAudioManagerRef.current) return;

    // Stop timer immediately by setting paused state FIRST
    setIsPaused(true);
    isActivelyRecordingRef.current = false;

    // Show processing state
    setIsFinalizingTranscription(true);

    // Pause capture and flush the server buffer so pending audio gets Phase 2
    await pauseCapture();

    // Wait a moment for any pending transcription to arrive from server
    await new Promise((resolve) => setTimeout(resolve, 2000));

    // Upload audio to blob storage (appends to existing)
    try {
      const snap = pcmAudioManagerRef.current.getUploadSnapshot();
      const allPCM = snap.pcm;
      const sampleRate = snap.sampleRate;

      if (allPCM.length > 0 && storeAudioInBlob && sessionIdRef.current) {
        const wavBlob = pcmToWav(allPCM, sampleRate);

        const formData = new FormData();
        formData.append('file', wavBlob, 'complete.wav');
        formData.append('capture_epoch_id', snap.captureEpochId);
        formData.append('capture_start_sample', String(snap.captureStartSample));
        formData.append('capture_end_sample', String(snap.captureEndSample));
        formData.append('capture_sample_rate', String(snap.sampleRate));

        const uploadResp = await fetch(`/api/sessions/${sessionIdRef.current}/upload-audio`, {
          method: 'POST',
          credentials: 'include',
          body: formData,
        });

        if (uploadResp.ok) {
          const uploadData = await uploadResp.json();
          if (uploadData.audio_url) {
            // Clean up old local URL if exists
            if (recordedAudioUrl?.startsWith('blob:')) {
              URL.revokeObjectURL(recordedAudioUrl);
            }
            setRecordedAudioUrl(uploadData.audio_url);
          }
          // Clear PCM data after successful upload
          pcmAudioManagerRef.current?.clearPCMData();
        } else {
          console.error('[Conversation] Pause upload failed:', uploadResp.status);
        }
      }
    } catch (error) {
      console.error('[Conversation] Error uploading audio on pause:', error);
    } finally {
      // Always clear finalization state, even on error
      setIsFinalizingTranscription(false);
    }

    setProvisionalText('');
    // isPaused already set at the start for immediate timer stop

    toast({
      title: 'Upptaka í bið',
      description: 'Smelltu á takka til að halda áfram...',
    });
  };

  // Resume audio capture after pause
  const resumeRecording = async () => {
    if (!pcmAudioManagerRef.current || !streamingClientRef.current) {
      // If refs are gone, we need to start fresh
      setIsRecording(true);
      isActivelyRecordingRef.current = true;
      setIsPaused(false);
      setIsInitializing(true);
      startRecording();
      return;
    }

    // Track message count at start of this recording segment
    messagesCountAtRecordingStartRef.current = messages.length;

    // Resume PCM capture (also clears the silence stopped-banner)
    try {
      await resumeCapture(selectedDeviceId);
      microphone.markGranted();
    } catch (e) {
      if (microphone.reportError(e)) return;
      console.error('[Conversation] Resume error:', e);
      toast({ title: 'Villa', description: 'Ekki tókst að halda upptöku áfram', variant: 'destructive' });
      return;
    }

    setIsPaused(false);
    isActivelyRecordingRef.current = true;

    toast({
      title: 'Upptaka heldur áfram',
      description: 'Samtal verður tekið upp...',
    });
  };

  // Toggle recording
  const toggleRecording = async () => {
    if (callRef.current) return; // Call consent controls own capture.
    if (!isRecording) {
      // Not recording at all - start new session
      setIsRecording(true);
      isActivelyRecordingRef.current = true;
      setIsPaused(false);
      setIsInitializing(true);
      startRecording();
    } else if (isPaused) {
      // Recording session exists but paused - resume
      resumeRecording();
    } else {
      // Actively recording - pause
      pauseRecording();
    }
  };

  const tutorialDemoNote = useTutorialDemoNote();

  // Generate summary using templates.
  const generateSummary = async (style?: SummaryStyle) => {
    if (isRecording && !isPaused) return;
    // SmartPanel disables its buttons without a template; this also covers the
    // automatic regenerate paths (language change etc.).
    if (selectedTemplateId == null) return;

    if (messages.length === 0) {
      toast({
        title: 'Engin skilaboð',
        description: 'Engin skilaboð til að gera samantekt úr.',
        variant: 'destructive',
      });
      return;
    }

    const noteRun = tutorialDemoNote.startNote();
    try {
      // For resumed/uploaded sessions or when streaming client doesn't have session ID,
      // call the conversation summary API directly
      if (isResumedSession || !streamingClientRef.current?.getSessionId()) {
        // Format messages as transcript for summary generation
        const transcript = messages
          .map((msg) => `${msg.speaker === 'doctor' ? 'Læknir' : 'Sjúklingur'}: ${msg.text}`)
          .join('\n');

        // Build segments array for attribution (includes IDs that match displayed messages)
        const segments = messages.map((msg) => ({
          id: msg.id,
          text: msg.text,
          speaker: msg.speaker,
          timestamp: msg.timestamp,
          end_timestamp: msg.end_timestamp,
        }));

        const data = await requestSummary({
          transcript,
          mode: 'conversation',
          template_id: selectedTemplateId ?? undefined,
          session_id: sessionIdRef.current || undefined, // Include for auto-save to ModelSummary
          segments, // Include segments for attribution (IDs match displayed messages)
          language: selectedSummaryLanguage,
          pre_encounter_context: preEncounterContextRef.current || undefined,
          style,
          regen_trigger: summaryGeneration.regenTrigger(selectedTemplateId, Boolean(summary)),
        });
        if (noteRun.isStale()) {
          return;
        }
        setSummary(data.soap_note);
        noteRun.markShown();
        summaryGeneration.recordFromHttp(data, selectedTemplateId);
        setEditedSummary(null);
        setAttributedSummary(data.attributed_summary ?? null);
        // Reconcile with the persisted task list (stop/pause detection writes it).
        refreshTasks();
      } else {
        // For live sessions, use streaming client
        setIsGeneratingSocketSummary(true);
        await streamingClientRef.current.generateSummary(
          selectedTemplateId,
          style,
          summaryGeneration.regenTrigger(selectedTemplateId, Boolean(summary))
        );
        // The summary will be received via the conversation_summary event.
        // Reconcile with the persisted task list (stop/pause detection writes it).
        refreshTasks();
      }
    } catch (error) {
      console.error('[Conversation] Error generating summary:', error);
      toast({
        title: 'Villa',
        description: error instanceof Error ? error.message : 'Ekki tókst að búa til samantekt',
        variant: 'destructive',
      });
      setIsGeneratingSocketSummary(false);
    }
  };

  const handleGenerateSummaryClick = (style?: SummaryStyle) => {
    void generateSummary(style);
  };

  // Handle summary edit
  const handleSummaryEdit = (text: string) => {
    setEditedSummary(text);
  };

  // Update attributedSummary parts with edited text while preserving segment_ids
  // Only text that matches the original keeps attribution; new/changed text becomes non-attributed
  const updateAttributedSummaryWithEditedText = (
    originalAttributed: AttributedSummaryData,
    editedText: string
  ): AttributedSummaryData => {
    if (!originalAttributed?.parts || originalAttributed.parts.length === 0) {
      return originalAttributed;
    }

    const originalParts = originalAttributed.parts;
    const originalText = originalParts.map((p) => p.text).join('');

    // If text hasn't changed, return original
    if (originalText === editedText) {
      return originalAttributed;
    }

    // Strategy: Find original parts that still exist in edited text (preserving order)
    // Text that doesn't match original becomes non-attributed
    const newParts: typeof originalParts = [];
    let editedPos = 0;

    for (const part of originalParts) {
      const partText = part.text;
      if (!partText) continue;

      // Find where this part's text appears in the remaining edited text
      const remainingEdited = editedText.slice(editedPos);
      const matchIndex = remainingEdited.indexOf(partText);

      if (matchIndex !== -1) {
        // Found the original text - check if there's new text before it
        if (matchIndex > 0) {
          // Add non-attributed part for the new text before
          const newText = remainingEdited.slice(0, matchIndex);
          newParts.push({
            text: newText,
            segment_ids: [], // No attribution for user-added text
          });
        }

        // Add the original part (preserved with attribution)
        newParts.push({
          ...part,
          text: partText,
        });

        editedPos += matchIndex + partText.length;
      }
      // If part text not found, it was deleted/modified - skip it
    }

    // Add any remaining text at the end as non-attributed
    if (editedPos < editedText.length) {
      const remainingText = editedText.slice(editedPos);
      if (remainingText) {
        newParts.push({
          text: remainingText,
          segment_ids: [], // No attribution for user-added text
        });
      }
    }

    // If no parts matched (complete rewrite), return single non-attributed part
    if (newParts.length === 0) {
      return {
        ...originalAttributed,
        has_attributions: false,
        parts: [{ text: editedText, segment_ids: [] }],
      };
    }

    return {
      ...originalAttributed,
      parts: newParts,
    };
  };

  // Persist a full note text to `targetSessionId` and make it the saved summary (throws on
  // failure). Shared by manual save and the note-instruction composer (apply/undo); a composer
  // reply that arrives after the page switched session must not land in the new one.
  const persistNoteText = async (text: string, targetSessionId: string) => {
    if (!targetSessionId || targetSessionId !== sessionIdRef.current) {
      throw new Error('Skipt var um lotu — breytingin var ekki vistuð');
    }
    if (!selectedTemplateId) {
      throw new Error('Velja þarf sniðmát til að vista samantekt');
    }
    // Update attributed summary with edited text while preserving segment_ids for hover
    const updatedAttributed = attributedSummary ? updateAttributedSummaryWithEditedText(attributedSummary, text) : null;
    await saveSummaryMutation.mutateAsync({
      sessionId: targetSessionId,
      summaryText: text,
      templateId: selectedTemplateId,
      source: 'save',
      summaryAttributed: updatedAttributed,
      viewingAsAuthAccountId,
      generationId: summaryGeneration.generationId(),
    });
    // Saved to the target session; if the page moved on meanwhile, leave the new session's editor alone.
    if (targetSessionId !== sessionIdRef.current) return;
    setSummary(text); // Update original
    setEditedSummary(null); // Clear edit state
    setAttributedSummary(updatedAttributed); // Update with edited text
    clearSummaryDraft(); // Clear localStorage draft
  };

  // Save user-edited summary
  const handleSummarySave = async () => {
    if (!editedSummary || !sessionIdRef.current || !selectedTemplateId) {
      toast({
        title: 'Velja þarf sniðmát',
        description: 'Veldu sniðmát til að vista samantekt.',
        variant: 'destructive',
      });
      return;
    }

    try {
      await persistNoteText(editedSummary, sessionIdRef.current);
      toast({ title: 'Samantekt vistuð' });
    } catch (error) {
      console.error('[Conversation] Error saving summary:', error);
      toast({
        title: 'Villa',
        description: error instanceof Error ? error.message : 'Ekki tókst að vista samantekt',
        variant: 'destructive',
      });
    }
  };

  // Persist + audit the copied summary (fire-and-forget; never blocks the clipboard copy)
  const handleSummaryCopy = (text: string) => {
    if (!text || !sessionIdRef.current || !selectedTemplateId) return;
    copySummaryMutation.mutate(
      {
        sessionId: sessionIdRef.current,
        summaryText: text,
        templateId: selectedTemplateId,
        source: 'copy',
        viewingAsAuthAccountId,
        generationId: summaryGeneration.generationId(),
      },
      { onError: (err) => console.error('[Conversation] copy-persist failed', err) }
    );
  };

  // Seek audio to a specific time (for summary-to-audio linking)
  const handleSeekToTime = (time: number) => {
    bottomBarAudioRef.current?.seekTo(time);
  };

  // Note: formatSSN, extractDOB, searchPatients, addToRecentPatients, and panel resize
  // are now handled by custom hooks/utilities defined earlier in the component

  // Mirror auth-org audio storage setting from /api/auth/user.
  useEffect(() => {
    setStoreAudioInBlob(!call && (user?.orgSettings?.storeAudioInBlob ?? false));
  }, [call, user?.orgSettings?.storeAudioInBlob]);

  // Get transcript text for smart panel
  const transcriptText = messages
    .map((m) => `${m.speaker === 'doctor' ? 'Læknir' : 'Sjúklingur'}: ${m.text}`)
    .join('\n');

  // Task detection (shared hook — DB load, server events, completion toggle)
  const { tasks, confirmedTasks, toggleCompletion, handleServerTasks, refreshTasks, updateTaskContent } = useTasks({
    sessionId: sessionIdRef.current || undefined,
    enabled: hasVerkefniAccess(user),
  });

  // Beta note-instruction composer: owned here so desktop + mobile panels share one history.
  const noteInstructions = useNoteInstructions({
    sessionId: sessionIdRef.current || undefined,
    patientId: selectedPatient?.dbId,
    persistNote: persistNoteText,
    currentNote: editedSummary ?? summary,
    currentTasks: tasks,
  });

  const hasVisibleTranscript =
    messages.some((message) => message.text.replace(/\[silence\]/gi, '').trim().length > 0) ||
    provisionalText.trim().length > 0;
  const showMobileRecordingButton = shouldShowCompactRecordingControl({
    isRecording,
    isInitializing,
    isPaused,
    isFinalizingTranscription,
    hasContent: hasVisibleTranscript,
  });
  const recordingDisabledReason = isViewingAsDelegate ? 'Ekki hægt að taka upp á vegum annars notanda' : undefined;
  // The first-run tour plays a fictional consultation in here, then removes it with any note made from it.
  const tutorialDemo: SessionTutorialDemo = {
    showLine: (line, index) => {
      tutorialDemoNote.markSampleShown();
      setMessages((current) => [
        ...current,
        { id: demoMessageId(index), speaker: line.speaker, text: line.text, timestamp: index * 4 },
      ]);
    },
    // Only what the tour added: the person's own messages, and a note not made from the sample, stay.
    clear: () => {
      setMessages((current) => current.filter((message) => !isDemoMessageId(message.id)));
      if (!tutorialDemoNote.clearSample()) return;
      setSummary('');
      setEditedSummary(null);
      setAttributedSummary(null);
      setSummaryTemplateInfo(null);
    },
  };

  // Show loading state when resuming a session
  if (isLoadingSession) {
    return (
      <SessionViewport>
        <Sidebar />
        <div className="flex flex-1 items-center justify-center">
          <div className="text-center">
            <div className="mx-auto mb-4 h-12 w-12 animate-spin rounded-full border-primary border-b-2" />
            <p className="text-muted-foreground">Hleð samtali...</p>
          </div>
        </div>
      </SessionViewport>
    );
  }

  return (
    <SessionViewport
      tutorial={{
        mode: 'conversation',
        demo: tutorialDemo,
        enabled: !resumeSessionId && !isViewingAsDelegate,
        paused: (isRecording && !isPaused) || isInitializing,
      }}
    >
      <Sidebar />

      <div className="flex min-w-0 flex-1 flex-col">
        <Header title="" centerContent={!isResumedSession ? <ModeSwitcher activeMode="conversation" /> : undefined}>
          <div className="flex items-center gap-2 md:gap-4">
            {/* Timer - desktop only, mobile timer is in patient info bar */}
            <div
              className={`hidden items-center gap-2 rounded-full border px-2.5 py-1.5 md:flex md:px-3 md:py-1.5 ${
                pipelineStalled && isRecording
                  ? 'border-amber-300 bg-amber-50 text-amber-700'
                  : connectionState === 'reconnecting' && isRecording
                    ? 'border-orange-300 bg-orange-50 text-orange-700'
                    : connectionState === 'disconnected' && isRecording
                      ? 'border-red-400 bg-red-100 text-red-700'
                      : isRecording && !isPaused
                        ? 'border-red-200 bg-red-50 text-red-600'
                        : isPaused
                          ? 'border-amber-200 bg-amber-50 text-amber-600'
                          : 'border-slate-200 bg-slate-50 text-slate-600'
              }`}
            >
              <div
                className={`h-2 w-2 rounded-full ${
                  pipelineStalled && isRecording
                    ? 'bg-amber-500'
                    : connectionState === 'reconnecting' && isRecording
                      ? 'animate-ping bg-orange-500'
                      : connectionState === 'disconnected' && isRecording
                        ? 'bg-red-600'
                        : isRecording && !isPaused
                          ? 'animate-pulse bg-red-500'
                          : isPaused
                            ? 'bg-amber-500'
                            : 'bg-slate-400'
                }`}
              />
              <span className="font-medium font-mono text-xs md:text-sm">
                {isLoadingSession || (isResumedSession && elapsedTime === 0 && recordedAudioUrl) ? (
                  <Loader2 className="h-3 w-3 animate-spin" />
                ) : (
                  formatTime(elapsedTime)
                )}
              </span>
            </div>

            {/* Mobile Settings Button - opens mic/language selection */}
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="ghost" size="sm" className="h-10 w-10 p-0 md:hidden [&_svg]:size-5.5">
                  <Settings />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-72 p-3" align="end">
                <div className="space-y-3">
                  <div className="space-y-1.5">
                    <span className="font-medium text-muted-foreground text-xs">Hljóðnemi</span>
                    <Select value={selectedDeviceId} onValueChange={setSelectedDeviceId}>
                      <SelectTrigger className="h-9 w-full text-sm">
                        <SelectValue placeholder="Veldu hljóðnema" />
                      </SelectTrigger>
                      <SelectContent>
                        {devices.map((device) => (
                          <SelectItem key={device.deviceId} value={device.deviceId} className="text-sm">
                            {device.label || `Hljóðnemi ${device.deviceId.slice(0, 5)}`}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1.5">
                    <span className="font-medium text-muted-foreground text-xs">Tungumál</span>
                    <LanguageSelector
                      languages={languages}
                      transcriptionLanguage={selectedLanguage}
                      summaryLanguage={selectedSummaryLanguage}
                      onTranscriptionChange={setSelectedLanguage}
                      onSummaryChange={setSelectedSummaryLanguage}
                      disabled={isRecording}
                    />
                  </div>
                </div>
              </PopoverContent>
            </Popover>

            {/* New Session Button */}
            <Button
              variant="outline"
              size="sm"
              className="h-10 gap-1.5 px-2.5 md:h-8 md:gap-1.5 md:px-3"
              onClick={() => {
                clearSelectedPatient();
                navigate('/session/new');
              }}
            >
              <Plus className="h-4 w-4" />
              <span className="sr-only sm:not-sr-only">Ný lota</span>
            </Button>
          </div>
        </Header>

        {call?.panel}
        <main className="flex min-h-0 flex-1 overflow-hidden">
          {/* Left Panel: Conversation - hidden on mobile when viewing assistant */}
          <div
            className={`flex min-h-0 min-w-0 flex-1 flex-col bg-white ${mobileView === 'assistant' ? 'hidden md:flex' : ''}`}
          >
            {/* Patient Info Bar */}
            <div className={layoutClasses.columnHeader}>
              <div className="min-w-0 flex-[1_1_200px] md:max-w-sm">
                {call ? (
                  <p className="text-sm">Sjúklingur: {selectedPatient?.name || 'Tengdur sjúklingur viðtals'}</p>
                ) : (
                  <PatientSearchSelect {...patientSearch} />
                )}
              </div>

              {!call && (
                <RecordingControls
                  audioQuality={audioQuality}
                  isRecording={isRecording}
                  isPaused={isPaused}
                  isInitializing={isInitializing}
                  isFinalizingTranscription={isFinalizingTranscription}
                  elapsedTime={elapsedTime}
                  onToggleRecording={toggleRecording}
                  selectedDeviceId={selectedDeviceId}
                  devices={devices}
                  onDeviceChange={setSelectedDeviceId}
                  isSmartReviewing={isSmartReviewing}
                  isViewingAsDelegate={isViewingAsDelegate}
                  showMobileRecordingButton={showMobileRecordingButton}
                />
              )}
            </div>

            {!call && (
              <MicrophoneNotice
                access={microphone}
                onRetry={retryMicrophone}
                disabled={isInitializing || (isRecording && !isPaused) || isViewingAsDelegate}
              />
            )}

            <PipelineStallBanner
              isStalled={pipelineStalled}
              isRecording={isRecording}
              onRecover={handleManualRecovery}
            />
            <RecordingSilenceBanner
              phase={silencePhase}
              secondsRemaining={silenceSecondsRemaining}
              onCancel={cancelSilenceCountdown}
              onResume={toggleRecording}
            />

            {/* Conversation Transcript - hidden on mobile when viewing assistant */}
            <ConversationTranscript
              messages={messages}
              provisionalText={provisionalText}
              isRecording={isRecording}
              isPaused={isPaused}
              className={`flex-1 ${mobileView === 'assistant' ? 'hidden md:block' : ''}`}
              highlightedSegmentIds={highlightedSegmentIds}
              isInitializing={isInitializing}
              isFinalizingTranscription={isFinalizingTranscription}
              onStartRecording={toggleRecording}
              isRecordingDisabled={isViewingAsDelegate || !!call}
              recordingDisabledReason={recordingDisabledReason}
              speakerTitles={getSpeakerTitles(user?.labels)}
            />

            {/* Mobile Bottom Nav */}
            <MobileSessionNavigation activeView={mobileView} onViewChange={setMobileView} />

            <SessionBottomBar
              languages={languages}
              transcriptionLanguage={selectedLanguage}
              summaryLanguage={selectedSummaryLanguage}
              onTranscriptionLanguageChange={setSelectedLanguage}
              onSummaryLanguageChange={setSelectedSummaryLanguage}
              languageDisabled={isRecording}
              recordedAudioUrl={recordedAudioUrl}
              onAudioDurationLoaded={(duration) => {
                if (isResumedSession && elapsedTime === 0 && duration > 0) {
                  setElapsedTime(Math.round(duration));
                }
              }}
              downloadFilename={`conversation_${Date.now()}.wav`}
              audioRef={bottomBarAudioRef}
              audioUpload={
                !call && (
                  <AudioUpload
                    mode="conversation"
                    language={selectedLanguage}
                    disabled={isRecording}
                    ensureSession={ensureSession}
                    onDiarizeComplete={(result) => {
                      const newMessages: ConversationMessage[] = result.segments.map((seg, i) => ({
                        id: `upload-${Date.now()}-${i}`,
                        speaker: seg.speaker as 'doctor' | 'patient',
                        text: seg.text,
                        timestamp: seg.start,
                      }));
                      setMessages((prev) => [...prev, ...newMessages]);
                      messageIdRef.current = messages.length + newMessages.length;

                      if (!result.file) return;

                      if (recordedAudioUrl) {
                        URL.revokeObjectURL(recordedAudioUrl);
                      }

                      const url = URL.createObjectURL(result.file);
                      setRecordedAudioUrl(url);
                    }}
                  />
                )
              }
            />
          </div>

          {/* Resize Handle - hidden on mobile */}
          <div className={layoutClasses.resizer} onMouseDown={handleMouseDown}>
            <div className="-left-1 -right-1 absolute inset-y-0 group-hover:bg-primary/10" />
          </div>

          {/* Right Panel: Smart Assistant - desktop only */}
          <div className={layoutClasses.panelColumn} style={{ width: `${rightPanelWidth}px` }}>
            <SmartPanel
              summary={summary}
              documents={documents}
              isProcessing={isProcessing}
              dictationMode={false}
              onToggleDictation={() => {}}
              dictationText=""
              onDictationChange={() => {}}
              transcript={transcriptText}
              onGenerateSummary={handleGenerateSummaryClick}
              isGeneratingSummary={isGeneratingSummary}
              isTranscriptionActive={isRecording && !isPaused}
              summaryTemplateInfo={summaryTemplateInfo}
              conversationTemplateId={selectedTemplateId}
              onConversationTemplateChange={handleTemplateChange}
              editedSummary={editedSummary}
              onSummaryEdit={handleSummaryEdit}
              onSummarySave={handleSummarySave}
              onCopySummary={handleSummaryCopy}
              noteInstructions={noteInstructions}
              isSavingSummary={isSavingSummary}
              attributedSummary={attributedSummary}
              onHighlightSegments={setHighlightedSegmentIds}
              onSeekToTime={handleSeekToTime}
              sessionId={sessionIdRef.current}
              patientName={selectedPatient?.name}
              patient={bloodTestPatient}
              tasks={tasks}
              confirmedTasks={confirmedTasks}
              onToggleTaskCompletion={toggleCompletion}
              onTaskContentSaved={updateTaskContent}
              onContextChange={handlePreEncounterContextChange}
            />
          </div>

          {/* Mobile Smart Assistant - shown when assistant view is selected */}
          {mobileView === 'assistant' && (
            <MobileAssistantShell onViewChange={setMobileView}>
              <SmartPanel
                summary={summary}
                documents={documents}
                isProcessing={isProcessing}
                dictationMode={false}
                onToggleDictation={() => {}}
                dictationText=""
                onDictationChange={() => {}}
                transcript={transcriptText}
                onGenerateSummary={handleGenerateSummaryClick}
                isGeneratingSummary={isGeneratingSummary}
                isTranscriptionActive={isRecording && !isPaused}
                summaryTemplateInfo={summaryTemplateInfo}
                conversationTemplateId={selectedTemplateId}
                onConversationTemplateChange={handleTemplateChange}
                editedSummary={editedSummary}
                onSummaryEdit={handleSummaryEdit}
                onSummarySave={handleSummarySave}
                onCopySummary={handleSummaryCopy}
                noteInstructions={noteInstructions}
                isSavingSummary={isSavingSummary}
                attributedSummary={attributedSummary}
                onHighlightSegments={setHighlightedSegmentIds}
                onSeekToTime={handleSeekToTime}
                sessionId={sessionIdRef.current}
                patientName={selectedPatient?.name}
                patient={bloodTestPatient}
                tasks={tasks}
                confirmedTasks={confirmedTasks}
                onToggleTaskCompletion={toggleCompletion}
                onTaskContentSaved={updateTaskContent}
                onContextChange={handlePreEncounterContextChange}
              />
            </MobileAssistantShell>
          )}
        </main>
      </div>
    </SessionViewport>
  );
}
// Build timestamp: 1768491224
